import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import type { ExpectedGroup, ExpectedList as Expected } from '../../lib/receiving';
import { fmtDay, fmtOrderedOn } from '../../lib/receiving';

/**
 * Screen 1 — Expected. A cumulative list: a line stays until it is received, cancelled or
 * switched, and nothing drops off overnight. Due today or late first (most late on top), then
 * expected later. The supplier is shown for information only — nobody picks one to receive.
 */
export function ExpectedList({ data }: { data: Expected | null }) {
  const { t } = useTranslation();
  if (!data) return <p className="text-lg text-muted-foreground">{t('rcv.loading')}</p>;
  const units = (gs: ExpectedGroup[]) => gs.reduce((n, g) => n + g.total, 0);
  return (
    <div className="space-y-6">
      <p className="text-base text-muted-foreground">{t('rcv.expected.intro')}</p>
      <Block
        title={t('rcv.expected.due')}
        units={units(data.due)}
        groups={data.due}
        empty={t('rcv.expected.noneDue')}
      />
      <Block
        title={t('rcv.expected.later')}
        units={units(data.later)}
        groups={data.later}
        empty={t('rcv.expected.noneLater')}
      />
    </div>
  );
}

function Block({
  title,
  units,
  groups,
  empty,
}: {
  title: string;
  units: number;
  groups: ExpectedGroup[];
  empty: string;
}) {
  const { t } = useTranslation();
  return (
    <section>
      <h3 className="mb-2 flex items-baseline gap-3 text-xl font-semibold">
        {title}{' '}
        <span className="text-base font-normal text-muted-foreground">
          {t('rcv.units', { count: units })}
        </span>
      </h3>
      {groups.length === 0 ? (
        <p className="rounded-lg border border-dashed p-4 text-base text-muted-foreground">
          {empty}
        </p>
      ) : (
        <div className="space-y-2">
          {groups.map((g) => (
            <Row key={g.productId} g={g} />
          ))}
        </div>
      )}
    </section>
  );
}

/**
 * Will the scanner recognise this part? The EANs above are the very codes a scan matches (the
 * catalogue's, or the ones found for the supplier part on EAN prep). Not ready → fix it there.
 */
function ScanBadge({ g }: { g: ExpectedGroup }) {
  const { t } = useTranslation();
  const tone: Record<ExpectedGroup['scanStatus'], string> = {
    READY: 'bg-success/[.12] text-success',
    REF_ONLY: 'bg-muted text-muted-foreground',
    NO_PO: 'bg-muted text-muted-foreground',
    NOT_READY: 'bg-destructive/10 text-destructive',
  };
  return (
    <span className={`rounded px-1.5 py-0.5 text-xs font-semibold ${tone[g.scanStatus]}`}>
      {t(`rcv.expected.scan.${g.scanStatus}`)}
      {g.scanStatus === 'NOT_READY' && (
        <>
          {' · '}
          <Link to="/warehouse/ean-prep" className="underline" onClick={(e) => e.stopPropagation()}>
            {t('rcv.expected.scan.fix')}
          </Link>
        </>
      )}
    </span>
  );
}

function Row({ g }: { g: ExpectedGroup }) {
  const { t } = useTranslation();
  return (
    <details className="group rounded-xl border bg-card">
      <summary className="flex min-h-12 cursor-pointer list-none flex-wrap items-center gap-x-6 gap-y-1 px-4 py-3">
        <div className="min-w-[16rem] flex-1">
          <div className="text-lg font-semibold">{g.name}</div>
          <div className="flex flex-wrap items-center gap-x-2 text-sm text-muted-foreground">
            <span>
              {g.brand} · {t('rcv.refShort')} {g.ref ?? '—'} ·{' '}
              {g.eans.length > 0 ? `EAN ${g.eans.join(', ')}` : 'EAN —'}
            </span>
            <ScanBadge g={g} />
          </div>
          <div className="text-sm text-muted-foreground">
            {t('rcv.expected.supplierInfo', { list: g.suppliers.join(', ') || '—' })}
          </div>
          {g.orderedBy.length > 0 && (
            <div className="text-sm">
              {t('rcv.expected.orderedBy')} <strong>{g.orderedBy.join(', ')}</strong>
            </div>
          )}
        </div>
        {g.maxLate > 0 && (
          <span className="rounded-md bg-destructive/15 px-2 py-1 text-sm font-semibold text-destructive">
            {t('rcv.expected.late', { count: g.maxLate })}
          </span>
        )}
        <div className="text-base">{g.split}</div>
        <div className="w-16 text-right text-3xl font-bold tabular-nums">{g.total}</div>
        <ChevronDown className="h-5 w-5 text-muted-foreground transition-transform group-open:rotate-180" />
      </summary>
      <div className="overflow-x-auto border-t px-4 py-3">
        <table className="w-full text-base">
          <thead className="text-left text-sm text-muted-foreground">
            <tr>
              <th className="py-1 pr-3">{t('rcv.expected.rank')}</th>
              <th className="py-1 pr-3">{t('rcv.expected.customer')}</th>
              <th className="py-1 pr-3">{t('rcv.expected.order')}</th>
              <th className="py-1 pr-3">{t('rcv.expected.qty')}</th>
              <th className="py-1 pr-3">{t('rcv.expected.promised')}</th>
              <th className="py-1 pr-3">{t('rcv.expected.orderedOn')}</th>
              <th className="py-1 pr-3">{t('rcv.expected.orderedByCol')}</th>
            </tr>
          </thead>
          <tbody>
            {g.customers.map((c) => (
              <tr key={c.lineId} className="border-t">
                <td className="py-2 pr-3">{c.rank}</td>
                <td className="py-2 pr-3">
                  {c.customerName ?? t('rcv.walkIn')}{' '}
                  <span className="rounded bg-muted px-1.5 py-0.5 text-xs">
                    {c.customerType === 'B2B' ? t('rcv.garage') : t('rcv.consumer')}
                  </span>
                </td>
                <td className="py-2 pr-3 font-semibold">
                  {c.orderNumber}
                  {c.orderedAtNotYetSent && (
                    <div className="text-xs font-normal text-amber-700 dark:text-amber-400">
                      {t('rcv.expected.notSent', { po: c.poNumber ?? '' })}
                    </div>
                  )}
                </td>
                <td className="py-2 pr-3">{c.qty}</td>
                <td className="py-2 pr-3">
                  {fmtDay(c.promisedAt)}
                  {c.late > 0 && (
                    <span className="ml-2 rounded bg-destructive/15 px-1.5 text-xs font-semibold text-destructive">
                      +{c.late} {t('rcv.expected.daysShort')}
                    </span>
                  )}
                </td>
                <td className="py-2 pr-3">{fmtOrderedOn(c.orderedAt)}</td>
                <td className="py-2 pr-3">
                  <strong>{c.orderedBy}</strong>
                  <div className="text-xs text-muted-foreground">{c.channelLabel}</div>
                </td>
              </tr>
            ))}
            {g.replenishment.map((r) => (
              <tr key={r.supplierOrderLineId} className="border-t">
                <td className="py-2 pr-3">—</td>
                <td className="py-2 pr-3" colSpan={2}>
                  <span className="rounded bg-sky-500/15 px-1.5 py-0.5 text-xs font-semibold text-sky-800 dark:text-sky-300">
                    {t('rcv.expected.replenishment')}
                  </span>{' '}
                  {t('rcv.expected.shelf', { location: r.location ?? '—' })} · {r.poNumber}
                </td>
                <td className="py-2 pr-3">{r.qty}</td>
                <td className="py-2 pr-3">{fmtDay(r.promisedAt)}</td>
                <td className="py-2 pr-3" />
                <td className="py-2 pr-3">{t('rcv.expected.purchasing')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </details>
  );
}
